// Vattenstämpel och verifieringskod som binder ett exporterat dokument till sitt projekt.
import { AlignmentType, Footer, PageNumber, Paragraph, TextRun } from "docx";
import { getActivePhase } from "@/hooks/useCurrentPhase";
import { isTrialWatermarkActive } from "@/lib/plan/freeTier";

export interface DocumentStamp {
  /** Skedet dokumentet exporterades i */
  phaseName?: string;
  /** Unik kod som slås upp på /verifiera */
  code: string;
  projectId: string | null;
  projectName: string;
  docNumber: string;
  documentName: string;
  exportedBy: string;
  exportedAt: string;
}

const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

/** AMK-XXXX-XXXX – lätt att läsa högt och skriva av från en utskrift. */
export const generateVerificationCode = (): string => {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  const chars = Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]);
  return `AMK-${chars.slice(0, 4).join("")}-${chars.slice(4).join("")}`;
};

export interface StampInput {
  projectId?: string | null;
  projectName?: string | null;
  docNumber?: string | null;
  documentName?: string | null;
  exportedBy?: string | null;
}

/** Stämpel med den inloggade användaren ifylld. */
export const createStampForCurrentUser = async (opts: StampInput): Promise<DocumentStamp> => {
  let exportedBy = opts.exportedBy || "";
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getUser();
    exportedBy = exportedBy || data.user?.email || "";
  } catch {
    /* stämpeln fungerar även utan namn */
  }
  return createStamp({ ...opts, exportedBy });
};

export const createStamp = (opts: {
  projectId?: string | null;
  projectName?: string | null;
  docNumber?: string | null;
  documentName?: string | null;
  exportedBy?: string | null;
}): DocumentStamp => ({
  code: generateVerificationCode(),
  projectId: opts.projectId ?? null,
  projectName: opts.projectName || "Okänt projekt",
  docNumber: opts.docNumber || "",
  documentName: opts.documentName || "",
  exportedBy: opts.exportedBy || "",
  exportedAt: new Date().toISOString(),
  phaseName: getActivePhase().name,
});

export const stampText = (stamp: DocumentStamp): string => {
  const date = new Date(stamp.exportedAt).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
  return [
    isTrialWatermarkActive() ? "UTKAST \u2013 PROVPERIOD, EJ VERIFIERAD VERSION" : null,
    `Projekt: ${stamp.projectName}`,
    stamp.docNumber ? `Dok ${stamp.docNumber}` : null,
    `Kod: ${stamp.code}`,
    stamp.phaseName ? `Skede: ${stamp.phaseName}` : null,
    `Exporterad ${date}${stamp.exportedBy ? ` av ${stamp.exportedBy}` : ""}`,
  ]
    .filter(Boolean)
    .join(" · ");
};

/** Sidfot med sidnummer, projektbindning och verifieringskod. */
export const stampFooter = (stamp: DocumentStamp): Footer =>
  new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({ text: "Sida ", size: 16, color: "666666" }),
          new TextRun({ children: [PageNumber.CURRENT], size: 16, color: "666666" }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new TextRun({ text: stampText(stamp), size: 14, color: "999999" })],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: "Dokumentet gäller endast för projektet ovan. Kontrollera koden på arbetsmiljokontrollen.se/verifiera",
            size: 14,
            color: "999999",
            italics: true,
          }),
        ],
      }),
    ],
  });

/** Synlig stämpelrad att lägga sist i dokumentet när sidfot saknas. */
export const stampParagraphs = (stamp: DocumentStamp): Paragraph[] => [
  new Paragraph({
    spacing: { before: 400 },
    children: [new TextRun({ text: stampText(stamp), size: 14, color: "999999" })],
  }),
  new Paragraph({
    children: [
      new TextRun({
        text: "Dokumentet gäller endast för projektet ovan. Kontrollera koden på arbetsmiljokontrollen.se/verifiera",
        size: 14,
        color: "999999",
        italics: true,
      }),
    ],
  }),
];
